import { create } from "zustand";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { useProjectStore } from "../core_stores/useProjectStore";

interface RollbackHandlerActions {
  startRollback: (checkpointId: string) => void;
  rollbackConfirmed: () => Promise<void>;
  reset: () => void;
  dispose: () => void;
}

interface RollbackHandlerState {
  checkpointId: string | null;
  shouldConfirmRollback: boolean;

  actions: RollbackHandlerActions;
}

const useRollbackHandlerStore = create<RollbackHandlerState>((set, get) => {
  let pendingJobDoneSubscription: (() => void) | null = null;

  return {
    checkpointId: null,
    shouldConfirmRollback: false,

    actions: {
      startRollback: (checkpointId: string) => {
        set({ shouldConfirmRollback: true, checkpointId });
      },

      rollbackConfirmed: async () => {
        // Clean up any existing pending subscription before creating a new one
        if (pendingJobDoneSubscription) {
          pendingJobDoneSubscription();
          pendingJobDoneSubscription = null;
        }

        pendingJobDoneSubscription = useProjectStore.subscribe(
          (state) => state.latestMsg,
          (latestMsg) => {
            if (
              latestMsg?.type ===
              SharedMessageTypes.MessageTypeEnum.enum.jobDone
            ) {
              if (pendingJobDoneSubscription) {
                pendingJobDoneSubscription();
                pendingJobDoneSubscription = null;
              }
              set({ checkpointId: null });
              set({ shouldConfirmRollback: false });
            }
          }
        );
        await useProjectStore.getState().actions.rollback(get().checkpointId!);
      },

      reset: () => {
        if (pendingJobDoneSubscription) {
          pendingJobDoneSubscription();
          pendingJobDoneSubscription = null;
        }
        set({ shouldConfirmRollback: false, checkpointId: null });
      },

      dispose: () => {
        if (pendingJobDoneSubscription) {
          pendingJobDoneSubscription();
          pendingJobDoneSubscription = null;
        }
        set({ shouldConfirmRollback: false, checkpointId: null });
      },
    },
  };
});

export const useShouldConfirmRollback = () =>
  useRollbackHandlerStore((state) => state.shouldConfirmRollback);

export const useRollbackHandlerActions = () =>
  useRollbackHandlerStore((state) => state.actions);
